import { Injectable } from '@angular/core';
import { ClientSignIn, ClientSignUp, PasswordData, ProfilePicture, UserDataStructure } from '@cfg-interface/authentication.interface';
import { catchError, Observable } from 'rxjs';
import { Auth, authState, createUserWithEmailAndPassword, EmailAuthCredential, EmailAuthProvider, GoogleAuthProvider, onAuthStateChanged, reauthenticateWithCredential, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, updateEmail, updatePassword, updateProfile, User, UserCredential } from '@angular/fire/auth';
import { Database, DatabaseReference, get, listVal, objectVal, push, ref, remove, set, update } from '@angular/fire/database';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(
              private auth: Auth,
              private database: Database,
              private httpClient: HttpClient
              ) { }

  public getAuthState(): Observable<User | null> {
    return authState(this.auth);
  }

  public async accessAsUser(data: ClientSignIn): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, data.email, data.password);
  }

  public async createNewUser(data: ClientSignUp): Promise<UserCredential> {
    return new Promise(async (resolve, reject) => {
      createUserWithEmailAndPassword(this.auth, data.email, data.password).then((response: UserCredential) => {
        onAuthStateChanged(this.auth, (user: User | null) => {
          updateProfile(response.user, {
            displayName: data.name,
            photoURL: '',
          });
          const AUTH_DATA_VALIDATION = {
            display_name: data.name,
            email: data.email,
            app: 'client'
          };
          const ADV_AS_JSON: string = JSON.stringify(AUTH_DATA_VALIDATION);
          this.httpClient.post(`${environment.cloud_functions_urls.sendWelcomeEmail}`, ADV_AS_JSON, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json'
            })
          }).pipe(catchError((errorTest:HttpErrorResponse): any => {
            if (errorTest.error instanceof ErrorEvent) {
              reject();
            } else {
              reject();
            }
          })).subscribe(($$response: any) => {
              resolve(response)
            });
          },(error: any) => {
          reject()
          });
          resolve(response);
          });
        });
  }

  // public async setSessionToken(user: User | null): Promise<void> {
  //   const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${user?.uid}/session_mngmnt/sessions`);
  //   const ID: number = Date.now();
  //   await push(DATABASE_REFERENCE, {
  //     session_token: ID,
  //     updated_at: new Date().toISOString()
  //   }).then(() => {
  //     localStorage.setItem('session_token', `${ID}`);
  //     this.startToMonitorSession(`${user?.uid}`);
  //   });
  // }

  public async setSessionToken(user: User | null): Promise<void> {
  const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${user?.uid}/session_mngmnt/sessions`);
  const ID: number = Date.now();

  const newSessionRef = push(DATABASE_REFERENCE); // Genera referencia antes de guardar

  await set(newSessionRef, {
    session_token: ID,
    updated_at: new Date().toISOString()
  });

  localStorage.setItem('session_token', `${ID}`);

  // Esperar hasta que Firebase confirme que la sesión está realmente escrita
  // antes de comenzar a monitorear (una pausa artificial también puede funcionar si es necesario)
  setTimeout(() => {
    this.startToMonitorSession(`${user?.uid}`);
  }, 500); // puedes ajustar este tiempo si es necesario
}

startToMonitorSession(uid: string): void {
  const SESSION_TOKEN = localStorage.getItem('session_token');
  const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/session_mngmnt/sessions`);

  let initialized = false;

  objectVal(DATABASE_REFERENCE).subscribe(async (response: any) => {
    if (!response) return;

    const sessionEntries = Object.entries(response) as [string, { session_token: number, updated_at: string }][];

    const sessionExists = sessionEntries.some(([_, data]) => `${data.session_token}` === SESSION_TOKEN);

    if (!initialized) {
      // Espera a que la sesión esté en la base antes de validar
      if (sessionExists) {
        initialized = true;
        return; // primera pasada, ya está mi sesión, ok
      }
      return; // aún no hay datos suficientes
    }

    // Ahora sí, luego de inicializado, validamos normalmente
    if (!sessionExists) {
      // console.warn('Sesión actual eliminada. Cerrando...');
      await this.signOut();
      return;
    }

    // Si hay más de 2 sesiones, eliminar la más antigua
    if (sessionEntries.length > 2) {
      sessionEntries.sort((a, b) =>
        new Date(a[1].updated_at).getTime() - new Date(b[1].updated_at).getTime()
      );
      const oldestSessionKey = sessionEntries[0][0];
      const DELETE_DATABASE_REFERENCE: DatabaseReference =
        ref(this.database, `CarFinderGeniePlatform/customers/${uid}/session_mngmnt/sessions/${oldestSessionKey}`);
      await remove(DELETE_DATABASE_REFERENCE);
    }
  });
}


  public async createDatabaseRegister(path: string, data: any): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `${path}`);
      await set(DATABASE_REFERENCE, data).then(() => {
        const CUSTOMER_UPDATE_DATA = {
          uid: data.uid,
          phone: data.phone
        };
        const PAYLOAD_AS_TEXT: string = JSON.stringify(CUSTOMER_UPDATE_DATA);
        this.httpClient.post(`${environment.cloud_functions_urls.updateUserPhoneNumber}`, PAYLOAD_AS_TEXT).pipe(catchError((errorTest:HttpErrorResponse): any => {
          if (errorTest.error instanceof ErrorEvent) {
            reject();
          } else {
            reject();
          }
        })).subscribe((response: any) => {}, (error: any) => { reject() }, () => { resolve() });
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  public async sendPasswordResetLink(email: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      sendPasswordResetEmail(this.auth, email).then(() => {
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });
  }

  public async signOut(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const token = localStorage.getItem('session_token');
      const uid = this.auth.currentUser?.uid;
      if (uid && token) {
        const sessionsRef = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/session_mngmnt/sessions`);
        const snapshot = await get(sessionsRef);
        const sessions = snapshot.val();
        if (sessions) {
          const sessionKeyToRemove = Object.entries(sessions).find(([_, value]: any) => `${value.session_token}` === token)?.[0];
          if (sessionKeyToRemove) {
            await remove(ref(this.database, `CarFinderGeniePlatform/customers/${uid}/session_mngmnt/sessions/${sessionKeyToRemove}`));
          }
        }
      }
      localStorage.removeItem('session_token');
      this.auth.signOut().then(() => {
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  public async deleteUserAccount(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.auth.onAuthStateChanged((user: User | null) => {
        user?.delete().then(() => {
          resolve();
        }).catch((error) => {
          reject();
        });
      });
    });
  }

  public async updateUserProfilePicture(url: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      onAuthStateChanged(this.auth, (user: User | null) => {
        updateProfile(user as User, {
          photoURL: url
        }).then(async () => {
          const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${user?.uid}/`);
          await update(DATABASE_REFERENCE, {
            photoURL: url
          }).then(() => {
            resolve();
          }).catch((error) => {
            reject();
          });
        }).catch((error) => {
          reject();
        });
      });
    });
  }

  public async updateUserEmailAccount(email: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const CURRENT_USER: User | null = this.auth.currentUser;
      updateEmail(CURRENT_USER as User, email).then(() => {
        const UID: string | undefined = CURRENT_USER?.uid;
        const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${UID}/`);
        update(DATABASE_REFERENCE, {
          email: email
        }).then(() => {
          resolve();
        }).catch((error: firebase.default.FirebaseError) => {
          reject();
        });
      }).catch((error: firebase.default.FirebaseError) => {
        reject();
      });
    });
  }

  public async updateUserPassword(password_data: PasswordData): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const CURRENT_USER: User | null = this.auth.currentUser;
      const CREDENTIALS: EmailAuthCredential = EmailAuthProvider.credential(password_data.email, password_data.currentPassword);
      reauthenticateWithCredential(CURRENT_USER as User, CREDENTIALS).then((value: UserCredential) => {
        updatePassword(CURRENT_USER as User, password_data.newPassword).then(() => {
          resolve();
        }).catch((error: firebase.default.FirebaseError) => {
          reject();
        });
      });
    });
  }

  public async updateUserProfile(name: string, country: string, phone: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const CURRENT_USER: User | null = this.auth.currentUser;
      updateProfile(CURRENT_USER as User, {
        displayName: name,
      });
      const UID: string | undefined = CURRENT_USER?.uid;
      const USER_UPDATE = {
        name: name,
        country: country,
        phone: phone
      };
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${UID}/`);
      update(DATABASE_REFERENCE, USER_UPDATE).then(() => {
        const CURRENT_HOST: string = location.hostname;
        const CUSTOMER_UPDATE_DATA = {
          uid: UID,
          phone: phone
        };
        const PAYLOAD_AS_TEXT: string = JSON.stringify(CUSTOMER_UPDATE_DATA);
        this.httpClient.post(`${environment.cloud_functions_urls.updateUserPhoneNumber}`, PAYLOAD_AS_TEXT).pipe(catchError((errorTest:HttpErrorResponse): any => {
          if (errorTest.error instanceof ErrorEvent) {
            reject();
          } else {
            reject();
          }
        })).subscribe((response: any) => {}, (error: any) => { reject() }, () => { resolve() });
        resolve();
      }).catch((error: firebase.default.FirebaseError) => {
        reject();
      });
    });
  }

  public async updateTermsAgreement(uid: string | undefined, terms: boolean): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/`);
      update(DATABASE_REFERENCE, {
        terms: terms
      }).then(() => {
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });
  }

  public getCustomers(): Observable<any[]> {
    const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/`);
    return listVal(DATABASE_REFERENCE);
  }

  public getCustomerData(uid: string): Observable<any> {
    const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/`);
    return objectVal<any>(DATABASE_REFERENCE);
  }

  public udpateNotificationsPermissions(value: boolean, uid: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/settings`);
      update(DATABASE_REFERENCE, {
        allow_notifications: value,
      }).then(() => {
        resolve();
      }).catch((error) => {
        reject({status: 'error', data: error});
      });
    });
  }

  public async setDeviceId(token: string, uid: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/`);
      update(DATABASE_REFERENCE, {
        device_id: token,
      }).then(() => {
        resolve({status: 'success'});
      }).catch((error) => {
        reject({status: 'error', data: error});
      });
    });
  }

  public async removeDeviceId(uid: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.database, `CarFinderGeniePlatform/customers/${uid}/device_id/`);
      remove(DATABASE_REFERENCE).then(() => {
        resolve();
      }).catch((error) => {
        reject({status: 'error', data: error});
      });
    });
  }

  public async signInWithGoogle(): Promise<UserCredential> {
    return new Promise(async (resolve, reject) => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      signInWithPopup(this.auth, provider)
        .then((result: UserCredential) => {
          try {
            const user = result.user;
            if (user) {
              const serializableUser: any =
                (typeof (user as any).toJSON === 'function')
                  ? (user as any).toJSON()
                  : {
                      uid: user.uid,
                      displayName: user.displayName,
                      email: user.email,
                      phoneNumber: user.phoneNumber,
                      photoURL: user.photoURL,
                      providerData: user.providerData
                    };
              localStorage.setItem('firebase_auth_user', JSON.stringify(user));
            }
          } catch (e) {
            // Si algo falla al serializar, no romper el flujo de login
          }
          resolve(result);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public async createOrUpdateGoogleUser(user: User): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(
        this.database,
        `CarFinderGeniePlatform/customers/${user.uid}`
      );

      try {
        const snapshot = await get(DATABASE_REFERENCE);

        // Buscar datos del proveedor de Google (donde realmente viene el displayName y la foto)
        const googleProvider =
          (user.providerData || []).find((p: any) => p && p.providerId === 'google.com') ||
          (user.providerData && user.providerData[0]) ||
          null;

        // Determinar nombre preferible: displayName global o del proveedor de Google, evitando usar el email completo
        let displayNameCandidate =
          user.displayName ||
          (googleProvider && googleProvider.displayName) ||
          '';

        if (displayNameCandidate && user.email && displayNameCandidate === user.email) {
          displayNameCandidate = '';
        }

        if (!displayNameCandidate && user.email) {
          displayNameCandidate = user.email.split('@')[0];
        }

        // Determinar foto de perfil preferible: photoURL global o del proveedor de Google
        const photoCandidate: string =
          user.photoURL ||
          (googleProvider && googleProvider.photoURL) ||
          '';

        if (snapshot.exists()) {
          // Usuario ya existe: actualizar timestamp y, si corresponde, el nombre/foto
          const existing: any = snapshot.val();
          const existingName: string = existing?.name || '';
          const existingEmail: string = existing?.email || '';
          const existingAuthProvider: string = existing?.auth_provider || '';

          const updateData: any = {
            timestamp: Date.now()
          };

          // Asegurar que el auth_provider quede marcado como 'google' si no estaba definido
          if (!existingAuthProvider) {
            updateData.auth_provider = 'google';
          }

          const isGoogleCustomer: boolean = (existingAuthProvider || updateData.auth_provider) === 'google';

          // Solo actualizar name si la cuenta es de Google y el nombre está vacío o es igual al email
          if (isGoogleCustomer && (!existingName || existingName === existingEmail)) {
            updateData.name = displayNameCandidate;
          }

          // Actualizar email/photoURL por si cambian
          updateData.email = user.email || existingEmail;
          updateData.photoURL = photoCandidate || existing?.photoURL || '';

          await update(DATABASE_REFERENCE, updateData);
          resolve();
          return;
        }

        // Usuario nuevo: crear registro completo
        const userData: UserDataStructure = {
          auth_provider: 'google',
          device_id: '',
          email: user.email || '',
          name: displayNameCandidate,
          phone: user.phoneNumber || '',
          photoURL: photoCandidate,
          timestamp: Date.now(),
          uid: user.uid,
          user_type: 'customer'
        };

        await set(DATABASE_REFERENCE, userData);

        const AUTH_DATA_VALIDATION = {
          display_name: displayNameCandidate || '',
          email: user.email || '',
          app: 'client'
        };
        const ADV_AS_JSON: string = JSON.stringify(AUTH_DATA_VALIDATION);

        this.httpClient.post(`${environment.cloud_functions_urls.sendWelcomeEmail}`, ADV_AS_JSON, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        }).pipe(catchError((errorTest: HttpErrorResponse): any => {
          if (errorTest.error instanceof ErrorEvent) {
            throw errorTest.error;
          } else {
            throw errorTest;
          }
        })).subscribe({
          next: () => resolve(),
          error: (error: any) => reject(error)
        });
      } catch (error) {
        reject(error);
      }
    });
  }

}
